/**
 * Deep link back into the native app from a page rendered inside the mobile
 * WebView (the Stripe Connect onboarding return being the first case).
 *
 * The scheme is `gigmi` on BOTH platforms — iOS registers it in
 * `ios/bapApp/Info.plist` (CFBundleURLSchemes) and Android in
 * `AndroidManifest.xml` as `android:scheme="gigmi"`. The `com.gigmi` /
 * `com.gigmi.app` split is the *bundle/package id* (store URLs), NOT the URL
 * scheme, so nothing here branches per platform.
 *
 * Android's intent-filter additionally pins `android:host="app"`, and mobile's
 * `DeepLinking` config prefixes are `['https://gigmiapp.com', 'gigmi://app']` —
 * so a link MUST include the `app` host to resolve on Android.
 *
 * The payout screen (`StripeOnboardingScreen`) is nested inside `StarterStack`,
 * whose linking path is `starter/:id`. Nested paths inherit that prefix, so the
 * resolvable URL is `gigmi://app/starter/<userId>/payouts` — a bare
 * `gigmi://app/payouts` does NOT resolve. Mobile's `deep-linking.test.js` locks
 * both facts down.
 */
const APP_SCHEME = "gigmi";
const APP_HOST = "app";

// `:id` sits in the MIDDLE of this path, so it is a template rather than a
// prefix the id gets appended to. Overridable for future re-pointing.
const DEFAULT_RETURN_PATH =
  process.env.REACT_APP_APP_RETURN_PATH || `${APP_HOST}/starter/:id/payouts`;

/**
 * Build a `gigmi://` deep link. `:id` in the path template is replaced with the
 * user id; if the template has no `:id` the id is appended, which keeps the
 * older `app/home` style of target working.
 */
export const buildAppDeepLink = (userId, path = DEFAULT_RETURN_PATH) => {
  if (path.includes(":id")) {
    // Without an id the templated route cannot resolve — fall back to opening
    // the app at its root rather than emitting a URL containing a literal ":id".
    if (!userId) {
      return `${APP_SCHEME}://${APP_HOST}`;
    }
    return `${APP_SCHEME}://${path.replace(":id", encodeURIComponent(userId))}`;
  }
  const base = `${APP_SCHEME}://${path}`;
  return userId ? `${base}/${userId}` : base;
};

/**
 * Navigate the WebView to the app deep link. Uses `window.location.href`
 * (not `assign`) because a custom-scheme navigation inside a WebView is handed
 * to the OS rather than loaded as a document — the page itself stays put, so
 * there is nothing to strip or clean up first.
 */
export const returnToApp = (userId, path = DEFAULT_RETURN_PATH) => {
  const url = buildAppDeepLink(userId, path);
  if (typeof window !== "undefined") {
    window.location.href = url;
  }
  return url;
};
